/**
 * useSBTContract.ts
 * ─────────────────────────────────────────────────────────────
 * All blockchain interactions for the .10ksquad SBT feature.
 *
 * Reads:
 *  - Whether the wallet holds ≥1 NFT from the 10K Squad collection
 *  - Whether the wallet has already minted an SBT
 *  - The minted name (if any)
 *  - Whether a given handle is already taken
 *
 * Writes:
 *  - mint(handle) — calls SBT contract's mint function
 *
 * SETUP:
 *  1. Deploy your SBT contract (see GUIDE.md for the Solidity source)
 *  2. Set VITE_SBT_CONTRACT_ADDRESS in your .env file
 *  3. The NFT collection address is already hardcoded below (read-only)
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback, useRef } from "react";
import SBT_ABI from "../lib/sbt-abi.json";

// ─── addresses ────────────────────────────────────────────────

/** 10K Squad NFT collection on Monad (read-only ownership check) */
const NFT_COLLECTION = "0x818030837e8350ba63e64d7dc01a547fa73c8279";

/** Your deployed SBT contract — set in .env */
const SBT_CONTRACT =
  (import.meta as any).env?.VITE_SBT_CONTRACT_ADDRESS ?? "";

/** Monad Testnet public RPC (used for read-only calls) */
const MONAD_RPC = "https://rpc.monad.xyz"

// ─── minimal ERC-721 balanceOf ABI ────────────────────────────

const ERC721_BALANCE_ABI = [
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
];

// ─── low-level JSON-RPC helpers ───────────────────────────────

/** Encode a eth_call payload (ABI encode is handled inline for simple cases) */
async function rpcCall(method: string, params: unknown[]): Promise<any> {
  const res = await fetch(MONAD_RPC, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.result;
}

/**
 * ABI-encode a single address argument (for balanceOf / ownerOf style calls).
 * Selector + padded address → hex string.
 */
function encodeAddressCall(selector: string, addr: string): string {
  // selector = first 4 bytes of keccak256(signature), passed as 0x{8chars}
  const cleanAddr = addr.toLowerCase().replace("0x", "").padStart(64, "0");
  return selector + cleanAddr;
}

/**
 * ABI-encode a single string argument (for isNameTaken / mintedNameOf calls).
 * This is a simplified encoder — works for short strings (< 32 bytes).
 */
function encodeStringCall(selector: string, str: string): string {
  const encoded = new TextEncoder().encode(str);
  const len = encoded.length.toString(16).padStart(64, "0");
  // offset to string data = 0x20
  const offset = "0000000000000000000000000000000000000000000000000000000000000020";
  // string bytes, padded to 32-byte boundary
  const padded = Array.from(encoded)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .padEnd(Math.ceil(encoded.length / 32) * 64, "0");
  return selector + offset + len + padded;
}

// function selectors (keccak256 of signature, first 4 bytes)
// These MUST match your deployed contract's function signatures exactly.
const SEL = {
  balanceOf:    "0x70a08231", // balanceOf(address)
  hasMinted:    "0x38e21cce", // hasMinted(address)
  mintedNameOf: "0x2dcc4827", // mintedNameOf(address)
  isNameTaken:  "0xb3da4e34", // isNameTaken(string)
};

// ─── decoding helpers ─────────────────────────────────────────

function decodeBool(hex: string): boolean {
  return parseInt(hex.replace("0x", "").slice(-64), 16) !== 0;
}

function decodeUint(hex: string): bigint {
  return BigInt("0x" + hex.replace("0x", "").slice(-64));
}

function decodeString(hex: string): string {
  try {
    const raw = hex.replace("0x", "");
    // offset at [0..63], length at [64..127], data from [128..]
    const lengthHex = raw.slice(64, 128);
    const length = parseInt(lengthHex, 16);
    const dataHex = raw.slice(128, 128 + length * 2);
    const bytes = new Uint8Array(
      dataHex.match(/.{2}/g)!.map((b) => parseInt(b, 16))
    );
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
}

// ─── hook ─────────────────────────────────────────────────────

export interface SBTContractState {
  holdsNFT: boolean;
  hasMintedSBT: boolean;
  mintedName: string;
  checkingEligibility: boolean;
  isMinting: boolean;
  txHash: string | null;
  mintError: string | null;
  checkAvailability: (handle: string) => Promise<boolean>;
  mint: (handle: string) => Promise<void>;
}

export function useSBTContract(address: string | null): SBTContractState {
  const [holdsNFT, setHoldsNFT] = useState(false);
  const [hasMintedSBT, setHasMintedSBT] = useState(false);
  const [mintedName, setMintedName] = useState("");
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);

  // ref to cancel stale eligibility checks
  const cancelRef = useRef(false);

  // ── check eligibility whenever address changes ──────────────

  useEffect(() => {
    if (!address) {
      setHoldsNFT(false);
      setHasMintedSBT(false);
      setMintedName("");
      return;
    }

    cancelRef.current = false;
    setCheckingEligibility(true);

    (async () => {
      try {
        // 1. Check NFT balance
        const balHex = await rpcCall("eth_call", [
          {
            to: NFT_COLLECTION,
            data: encodeAddressCall(SEL.balanceOf, address),
          },
          "latest",
        ]);
        if (cancelRef.current) return;
        const bal = decodeUint(balHex);
        setHoldsNFT(bal > 0n);

        if (bal === 0n) return;

        // 2. Check if already minted SBT (only if contract is deployed)
        if (!SBT_CONTRACT) return;

        const mintedHex = await rpcCall("eth_call", [
          {
            to: SBT_CONTRACT,
            data: encodeAddressCall(SEL.hasMinted, address),
          },
          "latest",
        ]);
        if (cancelRef.current) return;
        const minted = decodeBool(mintedHex);
        setHasMintedSBT(minted);

        if (minted) {
          // 3. Fetch their name
          const nameHex = await rpcCall("eth_call", [
            {
              to: SBT_CONTRACT,
              data: encodeAddressCall(SEL.mintedNameOf, address),
            },
            "latest",
          ]);
          if (cancelRef.current) return;
          setMintedName(decodeString(nameHex));
        }
      } catch (err) {
        console.error("Eligibility check failed:", err);
      } finally {
        if (!cancelRef.current) setCheckingEligibility(false);
      }
    })();

    return () => {
      cancelRef.current = true;
    };
  }, [address]);

  // ── check handle availability ───────────────────────────────

const checkAvailability = useCallback(async (handle: string): Promise<boolean> => {
  if (!SBT_CONTRACT) return true;
  try {
    const hex = await rpcCall("eth_call", [
      {
        to: SBT_CONTRACT,
        data: encodeStringCall(SEL.isNameTaken, handle),
      },
      "latest",
    ]);
    console.log("isNameTaken hex:", hex);
    return !decodeBool(hex);
  } catch (err) {
    console.error("checkAvailability failed:", err); // ← see the real error
    return true; // ← change to true so errors don't block minting
  }
}, []);

  // ── mint ────────────────────────────────────────────────────

  const mint = useCallback(
    async (handle: string) => {
      if (!address || !SBT_CONTRACT) {
        setMintError(
          !SBT_CONTRACT
            ? "SBT contract not deployed yet. Set VITE_SBT_CONTRACT_ADDRESS."
            : "Wallet not connected."
        );
        return;
      }

      setMintError(null);
      setTxHash(null);
      setIsMinting(true);

      try {
        const provider = (window as any).ethereum;
        if (!provider) throw new Error("No wallet found.");

        // Encode mint(string) call
        // selector for mint(string): must match your contract
        const MINT_SELECTOR = "0xd85d3d27"; // NOTE: update to match your contract's mint(string) selector
        const data = encodeStringCall(MINT_SELECTOR, handle);

        const hash: string = await provider.request({
          method: "eth_sendTransaction",
          params: [
            {
              from: address,
              to: SBT_CONTRACT,
              data,
              // gas is estimated by the wallet; you can hardcode if needed:
              // gas: "0x30D40", // 200,000
            },
          ],
        });

        setTxHash(hash);

        // Poll for receipt
        await waitForReceipt(hash);

        // Refresh state
        setHasMintedSBT(true);
        setMintedName(handle);
      } catch (err: any) {
        const msg =
          err?.message?.includes("user rejected")
            ? "Transaction rejected."
            : err?.message ?? "Mint failed. Please try again.";
        setMintError(msg);
      } finally {
        setIsMinting(false);
      }
    },
    [address]
  );

  return {
    holdsNFT,
    hasMintedSBT,
    mintedName,
    checkingEligibility,
    isMinting,
    txHash,
    mintError,
    checkAvailability,
    mint,
  };
}

// ─── receipt polling ───────────────────────────────────────────

async function waitForReceipt(
  hash: string,
  maxAttempts = 40,
  intervalMs = 2500
): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    await sleep(intervalMs);
    const receipt = await rpcCall("eth_getTransactionReceipt", [hash]);
    if (receipt) {
      if (receipt.status === "0x0") throw new Error("Transaction reverted.");
      return;
    }
  }
  throw new Error("Transaction timed out. Check the explorer manually.");
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
